"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import {
  ENTER_MS,
  EXIT_MS,
  TICK_MS,
  type HeroSlide,
  type HeroTimelineState,
  finishEnter,
  finishExit,
  initialHeroTimeline,
  requestSlide,
  requestStep,
  tickElapsed,
} from "./heroTimeline";

export function useHeroTimeline() {
  const reduced = usePrefersReducedMotion();
  const reducedRef = useRef(reduced);
  const pausedRef = useRef(false);
  const hiddenRef = useRef(false);
  const [state, setState] = useState<HeroTimelineState>(() => initialHeroTimeline(false));
  const stateRef = useRef(state);

  const commit = useCallback((recipe: (current: HeroTimelineState) => HeroTimelineState) => {
    setState((current) => {
      const next = recipe(current);
      stateRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    reducedRef.current = reduced;
    if (!reduced) return;
    commit((current) =>
      current.phase === "play" && current.elapsed === 0
        ? current
        : { ...current, phase: "play", elapsed: 0 },
    );
  }, [reduced, commit]);

  useEffect(() => {
    const update = () => {
      hiddenRef.current = document.visibilityState === "hidden";
    };
    update();
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (reducedRef.current || pausedRef.current || hiddenRef.current) return;
      commit((current) => tickElapsed(current, reducedRef.current));
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [commit]);

  useEffect(() => {
    if (reduced || state.phase === "play") return;
    const delay = state.phase === "exit" ? EXIT_MS : ENTER_MS;
    const id = window.setTimeout(() => {
      commit((current) => (current.phase === "exit" ? finishExit(current, reducedRef.current) : finishEnter(current)));
    }, delay);
    return () => window.clearTimeout(id);
  }, [state.phase, state.cycleId, reduced, commit]);

  const selectSlide = useCallback(
    (index: HeroSlide) => {
      commit((current) => requestSlide(current, index, reducedRef.current));
    },
    [commit],
  );

  const selectStep = useCallback(
    (index: number) => {
      commit((current) => requestStep(current, index));
    },
    [commit],
  );

  const setPaused = useCallback((paused: boolean) => {
    pausedRef.current = paused;
  }, []);

  return { ...state, reduced, selectSlide, selectStep, setPaused };
}
